import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { setGlobalErrorHandler } from '@/main';

export type FeedbackType = 'error' | 'warning' | 'info' | 'success';

export interface FeedbackState {
  isOpen: boolean;
  type: FeedbackType;
  title: string;
  message: string;
}

interface FeedbackContextValue {
  feedbackState: FeedbackState;
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
  showSuccess: (message: string, title?: string) => void;
  hideFeedback: () => void;
}

const FeedbackContext = createContext<FeedbackContextValue | undefined>(undefined);

export const FeedbackProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [feedbackState, setFeedbackState] = useState<FeedbackState>({
    isOpen: false,
    type: 'error',
    title: 'Error',
    message: '',
  });

  const showError = useCallback((message: string, title = 'Error') => {
    setFeedbackState({
      isOpen: true,
      type: 'error',
      title,
      message,
    });
  }, []);

  const showWarning = useCallback((message: string, title = 'Warning') => {
    setFeedbackState({
      isOpen: true,
      type: 'warning',
      title,
      message,
    });
  }, []);

  const showInfo = useCallback((message: string, title = 'Information') => {
    setFeedbackState({
      isOpen: true,
      type: 'info',
      title,
      message,
    });
  }, []);

  const showSuccess = useCallback((message: string, title = 'Success!') => {
    setFeedbackState({
      isOpen: true,
      type: 'success',
      title,
      message,
    });
  }, []);

  const hideFeedback = useCallback(() => {
    setFeedbackState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Register global error handler for TanStack Query mutations
  useEffect(() => {
    setGlobalErrorHandler(showError);
  }, [showError]);

  const value: FeedbackContextValue = {
    feedbackState,
    showError,
    showWarning,
    showInfo,
    showSuccess,
    hideFeedback,
  };

  return (
    <FeedbackContext.Provider value={value}>{children}</FeedbackContext.Provider>
  );
};

export const useFeedback = (): FeedbackContextValue => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within FeedbackProvider');
  }
  return context;
};
